import { Box, Indicator, Menu, Text } from '@mantine/core';
import { IconCopy, IconEdit, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { UserAvatar } from '@/components/user/UserAvatar';
import { Z_INDEX_DROPDOWN } from '@/constants/zIndex';
import { classNames } from '@/utils/helpers';
import type { CommentMessageProps, GroupPosition } from '../types';
import {
  COMMENT_MENU_ICON_SIZE_MD,
  COMMENT_MENU_ICON_SIZE_SM,
  COMMENT_MENU_WIDTH,
  PRESENCE_INDICATOR_AWAY_COLOR,
  PRESENCE_INDICATOR_COLOR,
  PRESENCE_INDICATOR_SIZE,
  PRESENCE_INDICATOR_OFFSET,
} from '../constants';
import styles from './CommentMessage.module.css';

function getBubblePositionClass(
  isCurrentUser: boolean,
  groupPosition: GroupPosition
): string {
  if (isCurrentUser) {
    if (groupPosition === 'first') return styles.bubbleRightFirst ?? '';
    if (groupPosition === 'middle') return styles.bubbleRightMiddle ?? '';
    if (groupPosition === 'last') return styles.bubbleRightLast ?? '';
    return styles.bubbleRightSolo ?? '';
  }
  if (groupPosition === 'first') return styles.bubbleLeftFirst ?? '';
  if (groupPosition === 'middle') return styles.bubbleLeftMiddle ?? '';
  if (groupPosition === 'last') return styles.bubbleLeftLast ?? '';
  return styles.bubbleLeftSolo ?? '';
}

export const CommentMessage = ({
  comment,
  isCurrentUser,
  authorPresenceStatus,
  groupPosition,
  editingCommentId,
  onEdit,
  onDelete,
  onCopy,
  formatDate,
}: CommentMessageProps) => {
  const { t } = useTranslation();
  const isDeleted = comment.isDeleted;
  const isEditing = editingCommentId === comment.id;
  const isFirstInGroup = groupPosition === 'solo' || groupPosition === 'first';
  // Empty author = identity stripped by a public share's personal-data flag.
  const authorDisplayName = comment.author || t('comments.anonymousAuthor');

  const wrapperClasses = classNames(
    styles.messageWrapper,
    isCurrentUser ? styles.messageWrapperRight : styles.messageWrapperLeft,
    isEditing && styles.messageWrapperEditing,
    isFirstInGroup && styles.messageWrapperFirstInGroup
  );

  const bubbleClasses = classNames(
    styles.messageBubble,
    isCurrentUser ? styles.messageBubbleRight : styles.messageBubbleLeft,
    getBubblePositionClass(isCurrentUser, groupPosition),
    isDeleted && styles.messageBubbleDeleted,
    isEditing && styles.messageBubbleEditing,
    isCurrentUser && !isDeleted && styles.messageBubbleClickable
  );

  return (
    <Box data-comment-id={comment.id} className={wrapperClasses}>
      {isFirstInGroup && (
        <Box className={styles.messageHeader}>
          {!isCurrentUser && !isDeleted && (
            <Text size="xs" fw={500} className={styles.authorName}>
              {authorDisplayName}
            </Text>
          )}
          <Text size="xs" c="dimmed" className={styles.headerTimestamp}>
            {formatDate(comment.createdAt)}
          </Text>
        </Box>
      )}

      <Box className={styles.messageRow}>
        {isFirstInGroup ? (
          <Indicator
            color={
              authorPresenceStatus === 'away'
                ? PRESENCE_INDICATOR_AWAY_COLOR
                : PRESENCE_INDICATOR_COLOR
            }
            position="bottom-end"
            size={PRESENCE_INDICATOR_SIZE}
            offset={PRESENCE_INDICATOR_OFFSET}
            withBorder
            disabled={!authorPresenceStatus}
            processing={false}
            className={styles.messageAvatar}
          >
            <UserAvatar
              displayName={authorDisplayName}
              avatarUrl={comment.authorAvatarUrl}
              size="sm"
            />
          </Indicator>
        ) : (
          <div className={styles.avatarSpacer} />
        )}

        <Box className={styles.messageContent}>
          <Menu
            shadow="md"
            width={COMMENT_MENU_WIDTH}
            zIndex={Z_INDEX_DROPDOWN}
          >
            <Menu.Target>
              <Box className={bubbleClasses}>
                <Text
                  size="sm"
                  className={`${styles.messageText} ${isDeleted ? styles.messageTextDeleted : ''}`}
                >
                  {isDeleted ? t('comments.messageDeleted') : comment.message}
                </Text>
              </Box>
            </Menu.Target>

            {!isDeleted && isCurrentUser && (
              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<IconCopy size={COMMENT_MENU_ICON_SIZE_SM} />}
                  onClick={() => onCopy(comment.message)}
                >
                  {t('common.copy')}
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconEdit size={COMMENT_MENU_ICON_SIZE_MD} />}
                  onClick={() => onEdit(comment)}
                >
                  {t('common.edit')}
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  leftSection={<IconTrash size={COMMENT_MENU_ICON_SIZE_SM} />}
                  color="red"
                  onClick={() => onDelete(comment.id)}
                >
                  {t('common.unsend')}
                </Menu.Item>
              </Menu.Dropdown>
            )}
          </Menu>

          {comment.isEdited && !isDeleted && (
            <Text
              size="xs"
              c="dimmed"
              fs="italic"
              className={styles.editedBadge}
            >
              {t('comments.edited')}
            </Text>
          )}
        </Box>
      </Box>
    </Box>
  );
};
